import React, { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { logout } from "../helpers/authHelper";
import { showError, showSuccess } from "../components/ToastNotifications";
import AuthShowcase, { AuthStatus, SubmitButton, TextField, useCooldown, validateEmail } from "../components/auth/AuthShowcase";

const tokenPattern = /^[a-f0-9]{64}$/i;

// Email verification and resending. `mode="resend"` opens straight on the resend form (/resend-verification).
const VerifyEmail = ({ mode }) => {
  const [token] = useState(() => {
    const raw = window.location.hash.slice(1);
    try { return decodeURIComponent(raw); } catch { return raw; }
  });
  const [searchParams] = useSearchParams();
  const emailInput = useRef(null);
  const initialEmail = searchParams.get("email") || "";
  const deliveryFailed = searchParams.get("delivery") === "failed";
  const [view, setView] = useState(() => mode === "resend" ? "resend" : token ? (tokenPattern.test(token) ? "verify" : "invalid") : initialEmail ? "check" : "resend");
  const [email, setEmail] = useState(initialEmail);
  const [fieldError, setFieldError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [cooldown, startCooldown] = useCooldown(60);

  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await apiRequest("/api/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) });
      // The verified account signs in next; drop any other account's session left in this browser.
      logout();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
      setView("verified");
      showSuccess(result.message || "Your email has been verified.");
    } catch (requestError) {
      setView(requestError.status === 400 ? "expired" : "verify");
      setError(requestError.message);
      showError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async (address) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      // The API answers the same way for unknown or already-verified addresses, so nothing is revealed.
      const result = await apiRequest("/api/auth/resend-verification", { method: "POST", body: JSON.stringify({ email: address }) });
      setEmail(address);
      setNotice(result.message);
      setView("check");
      startCooldown();
      showSuccess("Verification email requested.");
    } catch (requestError) {
      setError(requestError.message);
      showError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const submitResend = (event) => {
    event.preventDefault();
    const problem = validateEmail(email);
    setFieldError(problem);
    if (problem) { emailInput.current?.focus(); return; }
    resend(email.trim());
  };

  const resendForm = (heading, text) => <>
    <header className="auth-card-head">
      <h2>{heading}</h2>
      <p>{text}</p>
    </header>
    <form className="auth-form" onSubmit={submitResend} noValidate>
      {error && view === "resend" && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
      <TextField id="verification-email" label="Email address" icon="ti-mail" type="email" autoComplete="email" maxLength={254} placeholder="you@business.com"
        ref={emailInput} value={email} error={fieldError}
        onChange={(event) => { setEmail(event.target.value); if (fieldError) setFieldError(""); }}
        onBlur={() => email && setFieldError(validateEmail(email))} required />
      <SubmitButton busy={busy} busyLabel="Sending…" disabled={busy || cooldown > 0}>{cooldown > 0 ? `Resend in ${cooldown}s` : "Send verification email"}</SubmitButton>
    </form>
  </>;

  return (
    <AuthShowcase
      eyebrow="Secure your account"
      title={<>One quick step <em>to unlock your workspace.</em></>}
      intro="Verifying your email keeps your account secure and makes sure reset links and alerts reach you."
    >
      {view === "verified" ? <div className="auth-state">
        <AuthStatus tone="success" icon="ti-circle-check" />
        <h2>Email verified</h2>
        <p>Your email address is confirmed. Sign in to set up your business profile.</p>
        <Link className="auth-submit" to="/login">Continue to sign in <i className="ti ti-arrow-right" aria-hidden="true" /></Link>
      </div>

      : view === "verify" ? <div className="auth-state">
        <AuthStatus tone="info" icon="ti-mail-check" />
        <h2>Verify your email</h2>
        <p>Confirm this is your email address to finish creating your Socialyorix account.</p>
        {error && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
        <button type="button" className="auth-submit" onClick={verify} disabled={busy} aria-busy={busy || undefined}>
          {busy ? <><span className="auth-spinner" aria-hidden="true" /> Verifying…</> : <>Verify email address <i className="ti ti-arrow-right" aria-hidden="true" /></>}
        </button>
        <p className="auth-switch">Already verified? <Link className="auth-link" to="/login">Sign in</Link></p>
      </div>

      : view === "expired" || view === "invalid" ? <>
        <div className="auth-state is-compact">
          <AuthStatus tone="error" icon="ti-link-off" />
          <p role="alert">{view === "expired" ? error || "This verification link is invalid or has expired." : "This verification link is missing or incomplete."} Enter your email to get a new link.</p>
        </div>
        {resendForm("Get a new verification link", "We'll send a fresh link to your inbox.")}
        <p className="auth-switch">Already verified? <Link className="auth-link" to="/login">Sign in</Link></p>
      </>

      : view === "check" ? <div className="auth-state">
        <AuthStatus tone={deliveryFailed && !notice ? "warning" : "info"} icon={deliveryFailed && !notice ? "ti-mail-x" : "ti-mail-opened"} />
        <h2>{deliveryFailed && !notice ? "We couldn't send your email" : "Check your email"}</h2>
        <p role="status">{notice || (deliveryFailed
          ? <>We couldn't send a verification link to <strong>{email}</strong>. Try sending it again.</>
          : <>We sent a verification link to <strong>{email}</strong>. Open it to verify your address and continue.</>)}</p>
        <ul className="auth-tips">
          <li><i className="ti ti-folder" aria-hidden="true" /> Not there? Check spam or promotions.</li>
          <li><i className="ti ti-clock" aria-hidden="true" /> Links expire after a while; request a new one any time.</li>
        </ul>
        {error && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
        <button type="button" className="auth-submit is-secondary" disabled={busy || cooldown > 0} onClick={() => resend(email.trim())}>
          {busy ? <><span className="auth-spinner" aria-hidden="true" /> Sending…</> : cooldown > 0 ? <><i className="ti ti-clock" aria-hidden="true" /> Resend in {cooldown}s</> : <><i className="ti ti-mail-forward" aria-hidden="true" /> Resend verification email</>}
        </button>
        <p className="auth-switch"><button type="button" className="auth-link-button" onClick={() => { setView("resend"); setError(""); setNotice(""); }}>Use a different email</button> · <Link className="auth-link" to="/login">Sign in</Link></p>
      </div>

      : <>
        {resendForm("Resend verification email", "Enter the email you registered with and we'll send a new verification link.")}
        <p className="auth-switch">Need an account? <Link className="auth-link" to="/signup">Create one</Link> · <Link className="auth-link" to="/login">Sign in</Link></p>
      </>}
    </AuthShowcase>
  );
};

export default VerifyEmail;
